/**
 * Security › Blocking — the blocked and trusted address tables.
 *
 * Founder 2026-10-20: the block list grew to ~90 rows with no way to
 * find one. Both lists are now tables with search and pages. The API
 * returns every live row (caps 1,000 blocked / 500 trusted), so search
 * and paging run in the browser.
 */

import { useEffect, useMemo, useState } from 'react';

import type { AllowlistEntryWire, BlocklistEntryWire } from '@sill/types/api';

import { CountryFlag } from '../../components/atoms/CountryFlag.js';
import { fullPlaceLabel } from '../people/person-label.js';
import { useC } from '../../lib/theme.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export const ADDRESS_PAGE_SIZE = 25;

type Theme = ReturnType<typeof useC>;

/** Who added a row, in words. `user:<id>` is "You" for the viewer's own
 *  rows, "A teammate" otherwise; anything Sill does on its own is
 *  "Automatic" (the Source column already says which feature). */
export function describeAddedBy(created_by: string, current_user_id: string | null): string {
  if (created_by.startsWith('user:')) {
    return current_user_id !== null && created_by === `user:${current_user_id}` ? 'You' : 'A teammate';
  }
  if (created_by.startsWith('slack:')) return 'Slack';
  return 'Automatic';
}

/**
 * Prefer the server-resolved teammate name/email when the API carries
 * it (new field, `created_by_display`); otherwise fall back to the
 * generic words from {@link describeAddedBy}. The viewer's own rows
 * stay "You" so the table still highlights them.
 */
export function describeAddedByDisplay(
  row: { readonly created_by: string; readonly created_by_display?: string | null },
  current_user_id: string | null,
): string {
  // The viewer's own rows ALWAYS read "You" — the display string is a
  // teammate name, but the merchant knows they did this one themselves.
  if (current_user_id !== null && row.created_by === `user:${current_user_id}`) return 'You';
  const d = (row.created_by_display ?? '').trim();
  if (d.length > 0) return d;
  return describeAddedBy(row.created_by, current_user_id);
}

export function describeBlockSource(b: Pick<BlocklistEntryWire, 'provenance' | 'reason'>): string {
  if (b.provenance === 'campaign_autoblock') return 'Campaign auto-block';
  if (b.reason === 'manual') return 'Blocked by hand';
  if (b.reason.startsWith('threat_alert:')) return 'Suspicious sender';
  if (b.reason.startsWith('via_slack:')) return 'From Slack';
  return b.reason;
}

/** Trusted rows store a system reason (`threat_alert:<class>` when trusted
 *  from a sender's panel); show it in words, like blocked rows. */
export function describeTrustReason(reason: string): string {
  if (reason.startsWith('threat_alert:')) return 'Trusted from a suspicious sender';
  if (reason === 'manual') return 'Added by hand';
  return reason;
}

function formatUntil(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).format(d);
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(d);
}

/** Search + page state over a list. The page resets when the query
 *  changes and clamps when rows disappear (an Unblock on the last page). */
function usePagedSearch<T>(rows: readonly T[], haystack: (row: T) => string) {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (q === '') return rows;
    return rows.filter((r) => haystack(r).toLowerCase().includes(q));
    // `haystack` is a stable module-level shape per table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, query]);
  const pages = Math.max(1, Math.ceil(filtered.length / ADDRESS_PAGE_SIZE));
  useEffect(() => { setPage(0); }, [query]);
  useEffect(() => { if (page > pages - 1) setPage(pages - 1); }, [page, pages]);
  const start = Math.min(page, pages - 1) * ADDRESS_PAGE_SIZE;
  return {
    query, setQuery, page: Math.min(page, pages - 1), setPage, pages,
    total: filtered.length,
    start,
    visible: filtered.slice(start, start + ADDRESS_PAGE_SIZE),
  };
}

function SearchBox({ value, onChange, placeholder, testId, C }: {
  readonly value: string;
  readonly onChange: (v: string) => void;
  readonly placeholder: string;
  readonly testId: string;
  readonly C: Theme;
}): JSX.Element {
  return (
    <input
      type="search"
      data-testid={testId}
      aria-label={placeholder}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', maxWidth: 320, boxSizing: 'border-box',
        fontFamily: sansFont, fontSize: 13, color: C.text,
        background: C.surface, border: `1px solid ${C.borderSoft}`, borderRadius: 2,
        padding: '7px 10px', minHeight: 36,
      }}
    />
  );
}

function Pager({ page, pages, start, shown, total, onPage, testId, C }: {
  readonly page: number;
  readonly pages: number;
  readonly start: number;
  readonly shown: number;
  readonly total: number;
  readonly onPage: (p: number) => void;
  readonly testId: string;
  readonly C: Theme;
}): JSX.Element | null {
  if (total <= ADDRESS_PAGE_SIZE) return null;
  const btn = (disabled: boolean): React.CSSProperties => ({
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px', minHeight: 32,
    background: 'transparent', color: disabled ? C.muted : C.text,
    border: `1px solid ${C.border}`, borderRadius: 2,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1,
  });
  return (
    <div data-testid={testId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, marginRight: 'auto' }}>
        {start + 1}&ndash;{start + shown} of {total.toLocaleString('en-US')}
      </span>
      <button type="button" data-testid={`${testId}-prev`} disabled={page === 0} onClick={() => onPage(page - 1)} style={btn(page === 0)}>
        Previous
      </button>
      <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
        Page {page + 1} of {pages}
      </span>
      <button type="button" data-testid={`${testId}-next`} disabled={page >= pages - 1} onClick={() => onPage(page + 1)} style={btn(page >= pages - 1)}>
        Next
      </button>
    </div>
  );
}

function headCell(C: Theme): React.CSSProperties {
  return {
    textAlign: 'left', fontFamily: monoFont, fontSize: 9, letterSpacing: 2, fontWeight: 400,
    color: C.muted, padding: '8px 10px 8px 0', borderBottom: `1px solid ${C.border}`, whiteSpace: 'nowrap',
  };
}

function bodyCell(C: Theme): React.CSSProperties {
  return {
    fontFamily: sansFont, fontSize: 12, color: C.body,
    padding: '8px 10px 8px 0', borderBottom: `1px solid ${C.borderSoft}`, verticalAlign: 'middle',
  };
}

function rowButton(C: Theme, pending: boolean): React.CSSProperties {
  return {
    fontFamily: sansFont, fontSize: 12, padding: '5px 10px',
    background: 'transparent', color: C.text,
    border: `1px solid ${C.border}`, borderRadius: 2,
    cursor: pending ? 'wait' : 'pointer', whiteSpace: 'nowrap',
  };
}

export function BlockedAddressTable({ blocks, canMutate, pendingId, onUnblock, currentUserId, onOpenSender, loading }: {
  readonly blocks: readonly BlocklistEntryWire[];
  readonly canMutate: boolean;
  /** block_id being unblocked, if any. */
  readonly pendingId: string | null;
  readonly onUnblock: (block_id: string) => void;
  readonly currentUserId: string | null;
  /**
   * Founder bug 2026-10-20 (Report 6) — "blocklist rows must be
   * clickable to see details of what was blocked". When supplied,
   * a click on the row (not the Unblock button) opens Attackers for
   * that sender. The caller resolves the target: `source_threat_alert_id`
   * when present (opens the exact sender group), otherwise falls back
   * to filtering Attackers by the target address.
   */
  readonly onOpenSender?: (block: BlocklistEntryWire) => void;
  /**
   * Founder 2026-10-20: while the first list fetch is in flight the
   * caller passes `loading` so the empty copy ("No blocked addresses")
   * never flashes before the server has responded. Optional for
   * back-compat with callers that haven't been wired yet.
   */
  readonly loading?: boolean;
}): JSX.Element {
  const C = useC();
  const p = usePagedSearch(blocks, (b) => [
    b.target_display,
    fullPlaceLabel(b.location ?? null) ?? '',
    b.location?.country ?? '',
    describeBlockSource(b),
    describeAddedByDisplay(b, currentUserId),
  ].join(' '));

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted }}>
          BLOCKED ADDRESSES &middot; {blocks.length.toLocaleString('en-US')}
        </div>
        {blocks.length > 0 ? (
          <SearchBox value={p.query} onChange={p.setQuery} placeholder="Search address, place or source" testId="blocking-blocks-search" C={C} />
        ) : null}
      </div>
      {loading === true && blocks.length === 0 ? (
        <div
          data-testid="blocking-blocks-loading"
          aria-busy="true"
          style={{ marginTop: 6, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}
        >
          LOADING…
        </div>
      ) : blocks.length === 0 ? (
        <div style={{ marginTop: 6, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          No blocked addresses on this site.
        </div>
      ) : p.total === 0 ? (
        <div data-testid="blocking-blocks-empty-search" style={{ marginTop: 10, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          No blocked address matches “{p.query.trim()}”.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th scope="col" style={headCell(C)}>ADDRESS</th>
                  <th scope="col" style={headCell(C)}>LOCATION</th>
                  <th scope="col" style={headCell(C)}>SOURCE</th>
                  <th scope="col" style={headCell(C)}>ADDED BY</th>
                  <th scope="col" style={headCell(C)}>ADDED</th>
                  <th scope="col" style={headCell(C)}>UNTIL</th>
                  {canMutate ? <th scope="col" style={headCell(C)}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>Action</span></th> : null}
                </tr>
              </thead>
              <tbody data-testid="blocking-blocks-list">
                {p.visible.map((b) => {
                  const place = fullPlaceLabel(b.location ?? null);
                  const pending = pendingId === b.block_id;
                  const canOpen = onOpenSender !== undefined;
                  const openRow = (e: React.MouseEvent | React.KeyboardEvent): void => {
                    if (!canOpen) return;
                    // Don't swallow clicks inside the Unblock button
                    // (it has its own stopPropagation on tap).
                    const target = e.target as HTMLElement;
                    if (target.closest('button') !== null && target.closest('tr') !== null) {
                      const btn = target.closest('button');
                      if (btn?.getAttribute('data-testid') === 'blocking-unblock') return;
                    }
                    e.preventDefault();
                    onOpenSender(b);
                  };
                  return (
                    <tr
                      key={b.block_id}
                      data-testid="blocking-block-row"
                      data-block-id={b.block_id}
                      {...(canOpen ? {
                        onClick: openRow,
                        onKeyDown: (e: React.KeyboardEvent<HTMLTableRowElement>) => {
                          if (e.key === 'Enter' || e.key === ' ') openRow(e);
                        },
                        tabIndex: 0,
                        role: 'button' as const,
                        'aria-label': `View sender ${b.target_display}`,
                        style: { cursor: 'pointer' } as React.CSSProperties,
                      } : {})}
                    >
                      <td style={bodyCell(C)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                          {/* Founder 2026-10-19: always a flag; unknown country = muted block. */}
                          <span data-testid="blocking-block-flag" style={{ display: 'inline-flex', flexShrink: 0 }}>
                            <CountryFlag code={b.location?.country ?? 'ZZ'} />
                          </span>
                          <span style={{ fontFamily: monoFont, fontSize: 12, color: C.text, overflowWrap: 'anywhere' }}>
                            {b.target_display}
                          </span>
                        </span>
                      </td>
                      <td style={bodyCell(C)}>
                        {place !== null
                          ? <span data-testid="blocking-block-location">{place}</span>
                          : <span style={{ color: C.muted }}>Unknown</span>}
                      </td>
                      <td style={bodyCell(C)} data-testid="blocking-block-source">{describeBlockSource(b)}</td>
                      <td style={bodyCell(C)} data-testid="blocking-block-by">{describeAddedByDisplay(b, currentUserId)}</td>
                      <td style={{ ...bodyCell(C), whiteSpace: 'nowrap' }}>{formatShort(b.created_at)}</td>
                      <td style={{ ...bodyCell(C), whiteSpace: 'nowrap' }}>{b.expires_at === null ? 'Forever' : formatUntil(b.expires_at)}</td>
                      {canMutate ? (
                        <td style={{ ...bodyCell(C), textAlign: 'right', paddingRight: 0 }}>
                          <button
                            type="button"
                            data-testid="blocking-unblock"
                            aria-label={`Unblock ${b.target_display}`}
                            onClick={() => onUnblock(b.block_id)}
                            disabled={pending}
                            style={rowButton(C, pending)}
                          >
                            {pending ? 'Unblocking…' : 'Unblock'}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={p.page} pages={p.pages} start={p.start} shown={p.visible.length} total={p.total} onPage={p.setPage} testId="blocking-blocks-pager" C={C} />
        </>
      )}
    </div>
  );
}

export function TrustedAddressTable({ allowlist, canMutate, pendingId, onRemove, currentUserId, loading }: {
  readonly allowlist: readonly AllowlistEntryWire[];
  readonly canMutate: boolean;
  /** allow_id being removed, if any. */
  readonly pendingId: string | null;
  readonly onRemove: (allow_id: string) => void;
  readonly currentUserId: string | null;
  /**
   * Founder 2026-10-20: hold the "No trusted addresses" copy until the
   * first fetch settles. Optional for back-compat.
   */
  readonly loading?: boolean;
}): JSX.Element {
  const C = useC();
  const p = usePagedSearch(allowlist, (a) => [
    a.target_display,
    describeTrustReason(a.reason),
    describeAddedByDisplay(a, currentUserId),
  ].join(' '));

  return (
    <div data-testid="trusted-addresses">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ fontFamily: monoFont, fontSize: 9, letterSpacing: 2, color: C.muted }}>
          TRUSTED ADDRESSES &middot; {allowlist.length.toLocaleString('en-US')}
        </div>
        {allowlist.length > ADDRESS_PAGE_SIZE ? (
          <SearchBox value={p.query} onChange={p.setQuery} placeholder="Search trusted addresses" testId="blocking-allow-search" C={C} />
        ) : null}
      </div>
      {loading === true && allowlist.length === 0 ? (
        <div
          data-testid="blocking-allow-loading"
          aria-busy="true"
          style={{ marginTop: 6, fontFamily: monoFont, fontSize: 10, letterSpacing: 1, color: C.muted }}
        >
          LOADING…
        </div>
      ) : allowlist.length === 0 ? (
        <div style={{ marginTop: 6, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          No trusted addresses on this site.
        </div>
      ) : p.total === 0 ? (
        <div style={{ marginTop: 10, fontFamily: sansFont, fontSize: 12, color: C.muted }}>
          No trusted address matches “{p.query.trim()}”.
        </div>
      ) : (
        <>
          <div style={{ overflowX: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', minWidth: 520, borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th scope="col" style={headCell(C)}>ADDRESS</th>
                  <th scope="col" style={headCell(C)}>WHY</th>
                  <th scope="col" style={headCell(C)}>ADDED BY</th>
                  <th scope="col" style={headCell(C)}>ADDED</th>
                  {canMutate ? <th scope="col" style={headCell(C)}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>Action</span></th> : null}
                </tr>
              </thead>
              <tbody data-testid="blocking-allow-list">
                {p.visible.map((a) => {
                  const pending = pendingId === a.allow_id;
                  return (
                    <tr key={a.allow_id} data-testid="blocking-allow-row" data-allow-id={a.allow_id}>
                      <td style={{ ...bodyCell(C), fontFamily: monoFont, color: C.text, overflowWrap: 'anywhere' }}>{a.target_display}</td>
                      <td style={bodyCell(C)}>{describeTrustReason(a.reason)}</td>
                      <td style={bodyCell(C)}>{describeAddedByDisplay(a, currentUserId)}</td>
                      <td style={{ ...bodyCell(C), whiteSpace: 'nowrap' }}>{formatShort(a.created_at)}</td>
                      {canMutate ? (
                        <td style={{ ...bodyCell(C), textAlign: 'right', paddingRight: 0 }}>
                          <button
                            type="button"
                            data-testid="blocking-allow-remove"
                            aria-label={`Remove ${a.target_display}`}
                            onClick={() => onRemove(a.allow_id)}
                            disabled={pending}
                            style={rowButton(C, pending)}
                          >
                            {pending ? 'Removing…' : 'Remove'}
                          </button>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pager page={p.page} pages={p.pages} start={p.start} shown={p.visible.length} total={p.total} onPage={p.setPage} testId="blocking-allow-pager" C={C} />
        </>
      )}
    </div>
  );
}
